import React from "react";
import { ShieldAlert, Info, Ban } from "lucide-react";

const NOT_ACCEPTED = [
  "Mattresses",
  "Tyres",
  "Fridges / Freezers",
  "Electrical items",
  "Gas bottles",
  "Paint",
  "Chemicals",
  "Hazardous waste",
  "Asbestos",
  "Upholstered furniture",
];

/**
 * Reusable terms / disclaimer block displayed while building a quote or booking.
 * `variant` controls how prominent it is:
 *   - "inline" : small one-liner used inside the estimator widget
 *   - "card"   : full card with all bullet points (booking form)
 */
export default function TermsNotice({ variant = "card", className = "" }) {
  if (variant === "inline") {
    return (
      <div
        className={`mt-3 flex items-start gap-2 text-[11px] text-gray-400 leading-relaxed ${className}`}
        data-testid="terms-notice-inline"
      >
        <Info size={12} className="shrink-0 mt-0.5 text-[#8DC63F]" />
        <span>
          Items found on the day that aren&apos;t on your quote are charged at a higher walk-up rate or refused. We can&apos;t take mattresses, tyres, fridges, electricals, paint, gas bottles, upholstered furniture or hazardous waste. We may refuse collection, and offload already-loaded waste if payment isn&apos;t completed.
        </span>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <div
        className="rounded-xl border border-[#8DC63F]/40 p-4"
        style={{ background: "rgba(141, 198, 63, 0.07)" }}
        data-testid="terms-notice-card"
      >
        <div className="flex items-center gap-2 text-[#8DC63F]">
          <ShieldAlert size={16} />
          <div className="text-xs font-bold uppercase tracking-[0.18em]">
            Please read before booking
          </div>
        </div>
        <ul className="mt-2 space-y-1.5 text-sm text-gray-200 list-disc pl-5">
          <li>
            <span className="font-semibold text-white">Quote accuracy matters.</span>{" "}
            Anything found on the day that isn&apos;t on your quote will be priced at a{" "}
            <span className="font-semibold text-white">higher walk-up rate</span> on the day, or may be{" "}
            <span className="font-semibold text-white">refused</span> at our discretion.
          </li>
          <li>
            <span className="font-semibold text-white">We can refuse a collection</span>{" "}
            if the load is unsafe, hazardous, or materially different from what was booked.
          </li>
          <li>
            <span className="font-semibold text-white">No payment = no removal.</span>{" "}
            If the balance can&apos;t be paid after weighing, we reserve the right to{" "}
            <span className="font-semibold text-white">offload waste already loaded</span> on site.
          </li>
          <li>
            <span className="font-semibold text-white">Plasterboard must be separated</span>{" "}
            from all other waste — it has its own disposal route.
          </li>
        </ul>
      </div>

      <div
        className="rounded-xl border border-red-500/40 p-4"
        style={{ background: "rgba(239, 68, 68, 0.06)" }}
        data-testid="not-accepted-card"
      >
        <div className="flex items-center gap-2 text-red-300">
          <Ban size={16} />
          <div className="text-xs font-bold uppercase tracking-[0.18em]">We can&apos;t take</div>
        </div>
        <ul className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-sm text-gray-200 list-disc pl-5">
          {NOT_ACCEPTED.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
        <p className="mt-2 text-[11px] text-gray-400">
          Spotting these on the day will mean they&apos;re left behind. If you&apos;re unsure about something, please{" "}
          <a href="/contact" className="text-[#8DC63F] underline">contact us</a> before booking.
        </p>
      </div>
    </div>
  );
}
